import { useState, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { apiService } from "../services/api";
import { AuthService } from "../services/auth";
import { API_CONFIG } from "../config/constants";
import { ApiResponse, ApiError } from "../types/api";

interface UseThumbnailsOptions {
  // Defer fetching until the card is in view, to avoid a request stampede.
  enabled?: boolean;
}

interface UseThumbnailsReturn {
  thumbnails: string[];
  isLoading: boolean;
  error: ApiError | null;
}

const MAX_ATTEMPTS = 3;

// Throttling (429) and gateway/backend errors (>=500) are transient; the
// backend caps concurrency, so a retried request usually lands a moment later.
const isTransient = (status?: number): boolean =>
  status === 429 || (status !== undefined && status >= 500);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useThumbnails(
  videoKey: string,
  { enabled = true }: UseThumbnailsOptions = {},
): UseThumbnailsReturn {
  const auth = useAuth();
  // Read auth at fetch time so token refreshes don't re-trigger the effect.
  const authRef = useRef(auth);
  authRef.current = auth;

  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!enabled || !videoKey) return;

    let cancelled = false;

    const fetchThumbnails = async () => {
      if (!AuthService.isAuthenticated(authRef.current)) {
        if (!cancelled) {
          setError({ message: "User is not authenticated" });
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      const idToken = AuthService.getIdToken(authRef.current);
      const endpoint = `${API_CONFIG.endpoints.thumbnails}?key=${encodeURIComponent(videoKey)}`;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await apiService.get<ApiResponse<string[]>>(
            endpoint,
            idToken,
          );
          if (!cancelled) {
            setThumbnails(response.results || []);
            setIsLoading(false);
          }
          return;
        } catch (err) {
          const status = (err as ApiError)?.status;
          if (isTransient(status) && attempt < MAX_ATTEMPTS) {
            // Exponential backoff with jitter to spread out retried requests.
            await wait(2 ** attempt * 150 + Math.random() * 150);
            continue;
          }
          if (!cancelled) {
            setError({
              message:
                err instanceof Error
                  ? err.message
                  : "Failed to fetch thumbnails",
              status,
            });
            setThumbnails([]);
            setIsLoading(false);
          }
          return;
        }
      }
    };

    fetchThumbnails();

    return () => {
      cancelled = true;
    };
  }, [videoKey, enabled]);

  return { thumbnails, isLoading, error };
}
